/**
 * How a span of minutes is written wherever one is shown.
 *
 * Shared because it is read from `shared/client` itself — the routine trigger
 * labels format their meeting offset with it — and a module here cannot import
 * from either app. It also existed as byte-identical copies in web's
 * `calendars/timeOptions.ts` and native's `calendar/timeOptions.ts`, whose own
 * header asks for the two to be kept in sync by hand; both now re-export this.
 */

const HALF_HOUR = 30;
const HOUR = 60;
const MINUTES_IN_DAY = 24 * HOUR;

/** Compact duration label, unit joined to the number: "15min", "1h", "1h 30min". */
export function formatDurationLabel(mins: number) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}min`);
  return parts.join(" ") || "0min";
}

/**
 * The same span, written the way a routine's trigger states it.
 *
 * Spaced and spelled "hr" — "1.5 hr", not "1.5h" — because these sit in a
 * settings list the eye reads down, where the unit is part of the sentence the
 * row makes. {@link formatDurationLabel} keeps its tighter form for the event
 * time pickers, which pack a duration into a parenthetical beside a clock time
 * and have no room for the spaces.
 *
 * Two further departures. Past the hour it counts hours, half included, so the
 * column reads as one run of numbers rather than a two-part row wedged between
 * them. And a day is named rather than counted, since "1 day before" is how the
 * person setting it says what "24 hr" holds.
 *
 * Anything off the half-hour grid keeps both units: those come from an offset a
 * routine shipped rather than one a picker offered, and "1 hr 40 min" states it
 * exactly where "1.67 hr" would not.
 */
export function offsetMinutesLabel(minutes: number) {
  if (minutes === MINUTES_IN_DAY) return "1 day";
  // Anything short of an hour is stated as it stands, which also keeps a
  // negative offset readable: the routine form takes a free number and the
  // schema allows one, and decomposing -30 into hours and minutes drops both
  // parts and reads "0 min" — a different setting from the one stored.
  if (minutes < HOUR) return `${minutes} min`;
  if (minutes % HALF_HOUR === 0) return `${minutes / HOUR} hr`;
  return `${Math.floor(minutes / HOUR)} hr ${minutes % HOUR} min`;
}
